import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import { LoadingSpinner } from "@velaro/velaro-component-lib";

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
  }
}

interface WhatsAppConfig {
  id: number;
  tenantId: number;
  pageId: string;
  pageName: string;
  accessToken: string;
  phoneId: string;
  teamId?: number | null;
  teamName?: string | null;
}

function WhatsAppLogo({ size = 20 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
    </svg>
  );
}

export default function WhatsAppIntegration() {
  const [loading, setLoading] = useState(true);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [configs, setConfigs] = useState<WhatsAppConfig[]>([]);
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [provisioning, setProvisioning] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const api = useApi();

  const fetchConfigs = useCallback(async () => {
    try {
      const response = await api.messaging.get("WhatsApp/Integrations");
      const data = await response.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  // Load Meta SDK
  useEffect(() => {
    if ((window as any).FB) {
      setSdkLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.addEventListener("load", () => setSdkLoaded(true));
    document.body.appendChild(script);

    window.fbAsyncInit = function () {
      window.FB.init({
        appId: "559088305015876",
        autoLogAppEvents: true,
        xfbml: true,
        version: "v20.0"
      });
    };
  }, []);

  const launchWhatsAppSignup = useCallback(() => {
    if (!sdkLoaded || !(window as any).FB) {
      setMessage({ text: "Meta SDK not loaded. Please refresh and try again.", type: "error" });
      return;
    }
    setProvisioning(true);
    setMessage(null);

    window.FB.login(
      async (response: any) => {
        if (!response.authResponse) {
          setMessage({ text: "Authorization cancelled or failed.", type: "error" });
          setProvisioning(false);
          return;
        }
        try {
          const resp = await api.messaging.post("WhatsApp/Auth/GetBusinessId", {
            accessToken: response.authResponse.code,
            pageName: "",
            teamId: null
          });
          if (resp.ok) {
            setMessage({ text: "WhatsApp Business account connected successfully.", type: "success" });
            fetchConfigs();
          } else {
            setMessage({ text: "Failed to connect WhatsApp Business account. Please try again.", type: "error" });
          }
        } catch {
          setMessage({ text: "Failed to connect WhatsApp. Please try again.", type: "error" });
        } finally {
          setProvisioning(false);
        }
      },
      {
        config_id: "920701146565188",
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {} }
      }
    );
  }, [sdkLoaded, api.messaging, fetchConfigs]);

  const deleteConfig = useCallback(
    async (id: number) => {
      if (!window.confirm("Remove this WhatsApp account from Velaro?")) return;
      try {
        await api.messaging.delete(`WhatsApp/Integrations/${id}`);
        setConfigs((prev) => prev.filter((x) => x.id !== id));
      } catch {
        setMessage({ text: "Failed to remove account. Please try again.", type: "error" });
      }
    },
    [api.messaging]
  );

  if (loading) return <div className="flex justify-center py-20"><LoadingSpinner /></div>;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white"
          style={{ background: "#25D366" }}
        >
          <WhatsAppLogo size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-800">WhatsApp Business</h2>
          <p className="text-sm text-slate-500">
            Connect a WhatsApp Business account to receive and respond to WhatsApp messages in the
            Velaro inbox. Requires a Meta Business Manager account and a verified WhatsApp Business number.
          </p>
        </div>
      </div>

      {/* Status message */}
      {message && (
        <div
          className={`px-4 py-3 rounded-md text-sm ${
            message.type === "success"
              ? "bg-green-50 border border-green-200 text-green-800"
              : "bg-red-50 border border-red-200 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Phone ID explainer */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <i className="fas fa-circle-info text-blue-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="text-sm text-blue-800">
          <span className="font-semibold">What is the Phone Number ID?</span> When you connect
          WhatsApp via Meta, Meta assigns an internal "Phone Number ID" to your WhatsApp Business
          number. This ID (e.g. <code className="bg-blue-100 px-1 rounded font-mono text-xs">310326735507789</code>) is
          used by the WhatsApp Cloud API to route messages. Your actual phone number (e.g.{" "}
          <span className="font-medium">+1 800 xxx‑xxxx</span>) is visible in{" "}
          <strong>Meta Business Manager → WhatsApp Manager → Phone Numbers</strong>.
        </div>
      </div>

      {/* Connected accounts */}
      {configs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full bg-white border border-slate-200 rounded-lg text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Business / Page</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">
                  Phone Number ID
                  <span className="ml-1 text-slate-400 font-normal text-xs">(Meta internal)</span>
                </th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Team</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600" style={{ width: 1 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {configs.map((config) => (
                <tr key={config.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white"
                        style={{ background: "#25D366" }}
                      >
                        <WhatsAppLogo size={14} />
                      </div>
                      <div>
                        <div className="font-medium text-slate-800">{config.pageName || "WhatsApp Business"}</div>
                        {config.pageId && (
                          <div className="text-xs text-slate-400 font-mono">Page ID: {config.pageId}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-slate-100 px-2 py-0.5 rounded font-mono text-slate-700">
                      {config.phoneId || "—"}
                    </code>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {config.teamName ?? (config.teamId ? `Team ${config.teamId}` : "All teams")}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      Active
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      className="text-red-500 hover:text-red-700 underline text-xs"
                      onClick={() => deleteConfig(config.id)}
                      aria-label={`Remove ${config.pageName}`}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {configs.length === 0 && !loading && (
        <div className="text-center py-10 bg-slate-50 rounded-lg border border-dashed border-slate-200">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 text-white"
            style={{ background: "#25D366" }}
          >
            <WhatsAppLogo size={22} />
          </div>
          <p className="text-slate-600 font-medium">No WhatsApp Business accounts connected</p>
          <p className="text-slate-400 text-sm mt-1">
            Connect a WhatsApp Business account to receive messages in your inbox.
          </p>
        </div>
      )}

      {/* Connect button */}
      <div>
        <PrimaryBlueButton
          label={provisioning ? "Connecting..." : "Add WhatsApp Account"}
          onClick={launchWhatsAppSignup}
          disabled={provisioning}
        />
        <p className="text-xs text-slate-400 mt-2">
          You'll be guided through Meta's WhatsApp Business sign-up flow. You'll need admin access
          to your Meta Business Manager account and a phone number to register as your WhatsApp
          Business number.
        </p>
      </div>
    </div>
  );
}
